// ===========================================
// Two-Factor Authentication Section Component
// ===========================================
// Self-contained section for Account Settings page
// Manages its own dialog state (follows CalendarIntegrationSection pattern)

import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import {
  Shield as ShieldIcon,
  VerifiedUser as VerifiedUserIcon,
  DevicesOther as DevicesIcon,
  VpnKey as BackupIcon,
} from '@mui/icons-material';
import { use2FAStatus, useTrustedDevices } from '../../../hooks/useTwoFactor';
import { CARD_COLORS } from './account.constants';
import TwoFactorSetupDialog from './TwoFactorSetupDialog';
import TrustedDevicesDialog from './TrustedDevicesDialog';
import RegenerateBackupCodesDialog from './RegenerateBackupCodesDialog';
import DisableTwoFactorDialog from './DisableTwoFactorDialog';

interface Props {
  isFunMode: boolean;
}

export default function TwoFactorSection({ isFunMode }: Props) {
  // Dialog state
  const [showSetup, setShowSetup] = useState(false);
  const [showDevices, setShowDevices] = useState(false);
  const [showRegenerate, setShowRegenerate] = useState(false);
  const [showDisable, setShowDisable] = useState(false);

  // Data
  const { data: statusData, isLoading: statusLoading } = use2FAStatus();
  const { data: devicesData } = useTrustedDevices();

  const status = statusData?.data;
  const deviceCount = devicesData?.data?.devices?.length ?? 0;

  const cardSx = {
    mb: 3,
    ...(isFunMode && {
      borderRadius: 4,
      border: `3px solid ${CARD_COLORS.twoFactor.border}`,
      bgcolor: CARD_COLORS.twoFactor.bg,
      boxShadow: '4px 4px 0px rgba(0,0,0,0.08)',
    }),
  };

  // Loading state
  if (statusLoading) {
    return (
      <Card sx={cardSx}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  const isEnabled = status?.enabled;
  const iconColor = isFunMode ? CARD_COLORS.twoFactor.border : isEnabled ? 'success.dark' : 'text.disabled';
  const iconBgColor = isFunMode
    ? `${CARD_COLORS.twoFactor.border}30`
    : isEnabled
      ? 'success.light'
      : 'grey.200';

  return (
    <>
      <Card sx={cardSx}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Box
              sx={{
                bgcolor: iconBgColor,
                borderRadius: '50%',
                width: 48,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: iconColor,
                flexShrink: 0,
              }}
            >
              {isEnabled ? <VerifiedUserIcon /> : <ShieldIcon />}
            </Box>

            <Box sx={{ flex: 1 }}>
              {isEnabled ? (
                <>
                  {/* Enabled State */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="h6">Two-Factor Authentication</Typography>
                    <Chip label="Enabled" color="success" size="small" />
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {status?.enabledAt && (
                      <>Enabled on {new Date(status.enabledAt).toLocaleDateString()}</>
                    )}
                  </Typography>

                  {/* Status Chips */}
                  <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                    <Chip
                      icon={<BackupIcon />}
                      label={`${status?.backupCodesRemaining ?? 0} backup codes remaining`}
                      size="small"
                      variant="outlined"
                      color={(status?.backupCodesRemaining ?? 0) <= 2 ? 'warning' : 'default'}
                    />
                    <Chip
                      icon={<DevicesIcon />}
                      label={`${deviceCount} trusted device${deviceCount !== 1 ? 's' : ''}`}
                      size="small"
                      variant="outlined"
                    />
                  </Stack>

                  {/* Action Buttons */}
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DevicesIcon />}
                      onClick={() => setShowDevices(true)}
                    >
                      Manage Devices
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<BackupIcon />}
                      onClick={() => setShowRegenerate(true)}
                    >
                      Regenerate Backup Codes
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={() => setShowDisable(true)}
                    >
                      Disable 2FA
                    </Button>
                  </Stack>
                </>
              ) : (
                <>
                  {/* Disabled State */}
                  <Typography variant="h6">Two-Factor Authentication</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Add an extra layer of security to your account. When enabled, you'll need
                    to enter a code from your authenticator app in addition to your password.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<ShieldIcon />}
                    onClick={() => setShowSetup(true)}
                  >
                    Enable Two-Factor Authentication
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Dialogs */}
      <TwoFactorSetupDialog open={showSetup} onClose={() => setShowSetup(false)} />
      <TrustedDevicesDialog open={showDevices} onClose={() => setShowDevices(false)} />
      <RegenerateBackupCodesDialog open={showRegenerate} onClose={() => setShowRegenerate(false)} />
      <DisableTwoFactorDialog open={showDisable} onClose={() => setShowDisable(false)} />
    </>
  );
}
